import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChevronDown,
  CircleCheck,
  CircleDot,
  Clock3,
  SlidersHorizontal,
} from "lucide-react";
import api from "../../../services/api";
import { Table } from "../../../components/table";
import { DashboardKpiCard } from "../../../components/dashboard-kpi-card";
import { useToast } from "../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../utils";
import { ProtocolAccess } from "./access";
import { PartySelect } from "./party-select";
import {
  type Protocol,
  priorityLabels,
  statusLabels,
  slaLabels,
  protocolDate,
} from "../../../types/protocol";

const ADVANCED_FILTER_KEYS = [
  "sla",
  "assignedToSystemUserId",
  "requesterId",
  "from",
  "to",
  "sort",
  "order",
];

export default function ProtocolsPage() {
  return (
    <ProtocolAccess>
      <ProtocolList />
    </ProtocolAccess>
  );
}
function ProtocolList() {
  const [params, setParams] = useSearchParams();
  const { error } = useToast();
  const [rows, setRows] = useState<Protocol[]>([]);
  const [summary, setSummary] = useState({
    open: 0,
    inProgress: 0,
    resolved: 0,
    overdue: 0,
  });
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(() =>
    ADVANCED_FILTER_KEYS.some((key) => params.has(key)),
  );
  const loadVersion = useRef(0);
  const page = Math.max(1, Number(params.get("page")) || 1);
  const query = params.toString();
  const filter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("page", "1");
    setParams(next);
  };
  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setFailed(false);
    try {
      const [list, counters] = await Promise.all([
        api.get("/protocols", {
          params: Object.fromEntries(new URLSearchParams(query)),
        }),
        api.get("/protocols/summary"),
      ]);
      if (version !== loadVersion.current) return;
      setRows(list.data.data);
      setPages(list.data.totalPages);
      setSummary(counters.data);
    } catch (err) {
      if (version === loadVersion.current) {
        setFailed(true);
        setRows([]);
        error(getApiErrorMessage(err));
      }
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  }, [query, error]);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <div className="app-page-compact space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h1 className="app-page-title">Protocolos</h1>
        <Link
          className="btn btn-primary"
          to={`/painel/protocolos/novo${params.get("conversationId") ? `?conversationId=${params.get("conversationId")}` : ""}`}
        >
          Novo protocolo
        </Link>
      </div>
      <div
        className="grid grid-cols-2 xl:grid-cols-4 gap-3"
        aria-label="Indicadores"
      >
        <DashboardKpiCard
          icon={CircleDot}
          tone="blue"
          label="Abertos"
          value={summary.open}
        />
        <DashboardKpiCard
          icon={Clock3}
          tone="amber"
          label="Em andamento"
          value={summary.inProgress}
        />
        <DashboardKpiCard
          icon={CircleCheck}
          tone="emerald"
          label="Resolvidos"
          value={summary.resolved}
        />
        <DashboardKpiCard
          icon={Clock3}
          tone="rose"
          label="Vencidos"
          value={summary.overdue}
        />
      </div>
      <form
        className="app-card p-4 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          const data = new FormData(e.currentTarget);
          filter("search", String(data.get("search") || ""));
        }}
      >
        <div className="grid gap-3 md:grid-cols-3">
          <label className="form-label">
            Número, título ou descrição
            <input
              key={params.get("search") || "empty-search"}
              name="search"
              defaultValue={params.get("search") || ""}
              maxLength={200}
              className="form-input"
            />
          </label>
          <label className="form-label">
            Status
            <select
              className="form-input"
              value={params.get("status") || ""}
              onChange={(e) => filter("status", e.target.value)}
            >
              <option value="">Todos</option>
              {Object.entries(statusLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="form-label">
            Prioridade
            <select
              className="form-input"
              value={params.get("priority") || ""}
              onChange={(e) => filter("priority", e.target.value)}
            >
              <option value="">Todas</option>
              {Object.entries(priorityLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <button
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
          type="button"
          aria-expanded={advancedFiltersOpen}
          aria-controls="protocol-advanced-filters"
          onClick={() => setAdvancedFiltersOpen((open) => !open)}
        >
          <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
          Filtros avançados
          <ChevronDown
            className={`h-4 w-4 transition-transform ${advancedFiltersOpen ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
        {advancedFiltersOpen && (
          <div
            id="protocol-advanced-filters"
            className="grid gap-3 border-t border-slate-200 pt-4 md:grid-cols-2 xl:grid-cols-3"
          >
            <label className="form-label">
              Situação do SLA
              <select
                className="form-input"
                value={params.get("sla") || ""}
                onChange={(e) => filter("sla", e.target.value)}
              >
                <option value="">Todas</option>
                {Object.entries(slaLabels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <PartySelect
              kind="system-user"
              label="Responsável"
              value={params.get("assignedToSystemUserId") || ""}
              onChange={(value) => filter("assignedToSystemUserId", value)}
            />
            <PartySelect
              kind="user"
              label="Solicitante"
              value={params.get("requesterId") || ""}
              onChange={(value) => filter("requesterId", value)}
            />
            <label className="form-label">
              Abertura desde
              <input
                className="form-input"
                type="date"
                value={params.get("from")?.slice(0, 10) || ""}
                onChange={(e) =>
                  filter(
                    "from",
                    e.target.value ? `${e.target.value}T00:00:00-03:00` : "",
                  )
                }
              />
            </label>
            <label className="form-label">
              Abertura até
              <input
                className="form-input"
                type="date"
                value={params.get("to")?.slice(0, 10) || ""}
                onChange={(e) =>
                  filter(
                    "to",
                    e.target.value
                      ? `${e.target.value}T23:59:59.999-03:00`
                      : "",
                  )
                }
              />
            </label>
            <label className="form-label">
              Ordenar por
              <select
                className="form-input"
                value={params.get("sort") || "createdAt"}
                onChange={(e) => filter("sort", e.target.value)}
              >
                <option value="createdAt">Data de abertura</option>
                <option value="dueAt">Prazo</option>
              </select>
            </label>
            <label className="form-label">
              Ordem
              <select
                className="form-input"
                value={params.get("order") || "desc"}
                onChange={(e) => filter("order", e.target.value)}
              >
                <option value="desc">Decrescente</option>
                <option value="asc">Crescente</option>
              </select>
            </label>
            {params.get("conversationId") && (
              <p className="text-sm">Filtrado pelo atendimento de origem.</p>
            )}
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-4">
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => {
              setParams({});
              setAdvancedFiltersOpen(false);
            }}
          >
            Limpar filtros
          </button>
          <button className="btn btn-primary" type="submit">
            Pesquisar
          </button>
        </div>
      </form>
      {loading ? (
        <p role="status">Carregando…</p>
      ) : failed ? (
        <div role="alert">
          Falha ao carregar.{" "}
          <button className="btn btn-secondary" onClick={() => void load()}>
            Tentar novamente
          </button>
        </div>
      ) : (
        <Table<Protocol>
          data={rows}
          page={page}
          totalPages={pages}
          onPageChange={(value) => {
            const next = new URLSearchParams(params);
            next.set("page", String(value));
            setParams(next);
          }}
          columns={[
            {
              key: "protocolNumber",
              label: "Número",
              render: (p) => (
                <Link
                  className="text-blue-700 underline"
                  to={`/painel/protocolos/${p.id}`}
                >
                  {p.protocolNumber}
                </Link>
              ),
            },
            { key: "title", label: "Título" },
            {
              key: "requesterName",
              label: "Solicitante",
              render: (p) => p.requesterName || "Não informado",
            },
            {
              key: "priority",
              label: "Prioridade",
              render: (p) => priorityLabels[p.priority],
            },
            {
              key: "status",
              label: "Status",
              render: (p) => statusLabels[p.status],
            },
            {
              key: "assignedToName",
              label: "Responsável",
              render: (p) => p.assignedToName || "Sem responsável",
            },
            {
              key: "createdAt",
              label: "Abertura",
              render: (p) => protocolDate(p.createdAt),
            },
            {
              key: "dueAt",
              label: "Prazo",
              render: (p) => protocolDate(p.dueAt),
            },
            {
              key: "sla",
              label: "SLA",
              render: (p) => (
                <span
                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                    p.sla === "OVERDUE"
                      ? "bg-red-100 text-red-700"
                      : p.sla === "WARNING"
                        ? "bg-amber-100 text-amber-800"
                        : p.sla === "ADEQUATE"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {p.sla ? slaLabels[p.sla] : "Não se aplica"}
                </span>
              ),
            },
          ]}
          emptyMessage="Nenhum protocolo encontrado."
        />
      )}
    </div>
  );
}
